import type { Faro, LogLevel } from '@grafana/faro-react';

// The SDK is deliberately NOT imported as a value here. A value import would pull the whole web
// SDK (and its ESM-only web-vitals dependency) into the import graph of every service that
// reports telemetry, which breaks the Jest transform for those services' unit tests. Instead
// observability/faro.ts hands the initialised instance over once. `import type` is erased at
// compile time, so the typing stays exact while the runtime dependency stays in faro.ts.
let instance: Faro | undefined;

/** Called once by observability/faro.ts after initializeFaro succeeds. */
export function registerFaroInstance(faro: Faro): void {
	instance = faro;
}

/** Mirrors @grafana/faro-core's LogLevel; kept local so this module imports no SDK value. */
export enum TelemetryLogLevel {
	Trace = 'trace',
	Debug = 'debug',
	Info = 'info',
	Log = 'log',
	Warn = 'warn',
	Error = 'error',
}

/**
 * Named signals we push to Grafana. Keeping them in one enum means the dashboard/alert queries
 * have a fixed vocabulary instead of grepping free-form strings.
 */
export enum TelemetryEvent {
	// Offline command queue — the core of this offline-first PWA. None of this was observable
	// before; failures only ever reached the device console.
	OfflineCommandEnqueued = 'offline_command_enqueued',
	OfflineCommandSynced = 'offline_command_synced',
	OfflineCommandExhausted = 'offline_command_exhausted',
	// Still retrying, but it has failed often enough to report. Uploads never reach
	// "exhausted" on transient errors, so this is their only failure signal.
	OfflineCommandStalled = 'offline_command_stalled',
	OfflineCommandDiscarded = 'offline_command_discarded',
	// Start-up housekeeping of the offline store: orders evicted after going unopened, failed
	// uploads whose bytes were dropped.
	OfflineStoreRetention = 'offline_store_retention',
	// The legacy host queue (offline_db_v2) was moved into the package's queue after an upgrade.
	OfflineLegacyQueueImported = 'offline_legacy_queue_imported',
	OfflineStorageWriteFailed = 'offline_storage_write_failed',
	OfflineKeyMismatch = 'offline_key_mismatch',
	OfflineHandlerMissing = 'offline_handler_missing',
	FileZeroBytes = 'file_zero_bytes',

	// Device / connectivity.
	ConnectivityChanged = 'connectivity_changed',

	// SignalR hub lifecycle. Reported structurally because SignalR logs every drop through
	// console.error, and the console instrumentation turns those into exceptions — on a field app
	// that loses connectivity by design, that was the entire exception stream.
	SignalRStateChanged = 'signalr_state_changed',

	// PWA and micro-frontend delivery.
	ServiceWorkerInstalled = 'service_worker_installed',
	ServiceWorkerUpdated = 'service_worker_updated',
	AssetLoadFailed = 'asset_load_failed',

	// Health of the offline telemetry buffer itself — tells us whether a quiet period in the
	// data means "nothing happened" or "signals were dropped".
	TelemetryBufferDrained = 'telemetry_buffer_drained',
}

export enum TelemetryMeasurement {
	/** { depth, oldestAgeMs } — how much unsynced work a technician is carrying. */
	OfflineQueue = 'offline_queue',
	/** { durationMs, commandCount } — one full sync loop. */
	CommandSync = 'command_sync',
	/** { usageBytes, quotaBytes, usedPct } — IndexedDB pressure; photos fill this up. */
	StorageUsage = 'storage_usage',
}

type AttributeValue = string | number | boolean | null | undefined;
export type TelemetryAttributes = Record<string, AttributeValue>;

// Faro only accepts string attribute values. Normalising centrally means call sites can pass
// numbers/booleans/undefined without every one of them remembering to stringify.
function normalize(attributes?: TelemetryAttributes): Record<string, string> | undefined {
	if (!attributes) {
		return undefined;
	}

	return Object.entries(attributes).reduce<Record<string, string>>((acc, [key, value]) => {
		if (value !== undefined && value !== null) {
			acc[key] = String(value);
		}
		return acc;
	}, {});
}

// Every helper is a no-op until Faro is initialised (unit tests, or a build with no collector
// URL configured), so instrumentation can be called unconditionally from anywhere.

// skipDedupe on events and measurements: Faro drops a signal that deep-equals the immediately
// preceding one, which is right for repeated exceptions but would silently undercount these —
// syncing three identical commands in a row must show as three, not one.
export function trackEvent(name: TelemetryEvent, attributes?: TelemetryAttributes): void {
	instance?.api?.pushEvent(name, normalize(attributes), undefined, { skipDedupe: true });
}

export function trackMeasurement(type: TelemetryMeasurement, values: Record<string, number>, context?: TelemetryAttributes): void {
	instance?.api?.pushMeasurement({ type, values }, { context: normalize(context), skipDedupe: true });
}

// Dedupe is intentionally left on for errors: a fault in a retry loop would otherwise flood.
export function trackError(error: Error, context?: TelemetryAttributes, type?: string): void {
	instance?.api?.pushError(error, { type, context: normalize(context) });
}

export function trackLog(message: string, level: TelemetryLogLevel = TelemetryLogLevel.Info, context?: TelemetryAttributes): void {
	// TelemetryLogLevel mirrors LogLevel value-for-value; TypeScript still treats two enums as
	// distinct nominal types, hence the cast.
	instance?.api?.pushLog([message], { level: level as unknown as LogLevel, context: normalize(context) });
}

/** Coerces whatever was thrown into an Error so it reaches Grafana with a usable stack. */
export function toError(value: unknown, fallbackMessage: string): Error {
	if (value instanceof Error) {
		return value;
	}
	const error = new Error(`${fallbackMessage}: ${String(value)}`);
	error.name = fallbackMessage;
	return error;
}
